import {
    patchStudent,
    postCreateStudentLogin,
    postClassCreateStudent,
    postResetStudentLogin,
    deleteStudent,
    putUsersConvertToPassport,
    patchClass,
} from '@yandex-int/k-common/client/api';
import { showNetworkError } from 'platform-components';
import { retrySaga } from 'platform-components/utils';
import { all, call, put, takeLatest, takeEvery } from 'redux-saga/effects';

import { getTeamIdFromPath } from 'utils/path';
import { takeFirstCancelable } from 'utils/saga';

import { getTeam } from '../classes/actions';

import {
    editStudentNameError,
    editStudentNameSuccess,
    getNewStudentLoginSuccess,
    saveNewStudentSuccess,
    setCurrentEditingStudentIndex,
    resetStudentLoginSuccess,
    deleteStudentSuccess,
    setSubmitting,
    setRequesting,
    putUsersConvertToPassportSuccess,
    putUsersConvertToPassportError,
    putUserConvertToPassportSuccess,
    putUserConvertToPassportError,
} from './actions';
import {
    EDIT_STUDENT_NAME_REQUEST,
    SAVE_NEW_STUDENT_REQUEST,
    RESET_STUDENT_LOGIN_REQUEST,
    DELETE_STUDENT_REQUEST,
    GET_NEW_STUDENT_LOGIN_SUCCESS,
    CANCEL_ADDING_NEW_STUDENT,
    GET_NEW_STUDENT_LOGIN_REQUEST,
    CONVERT_STUDENTS_PASSPORT_REQUEST,
    CONVERT_STUDENT_PASSPORT_REQUEST,
} from './constants';
import { NEW_STUDENT_ID } from './students.constants';
import { setStudentPasswordToLocalStorageWithExpireTime } from './utils';

export function* studentsSagasWatcher() {
    yield all([
        editStudentNameWatcher(),
        getNewStudentLoginWatcher(),
        resetStudentLoginWatcher(),
        deleteStudentWatcher(),
        saveNewStudentWatcher(),
        putUsersConvertToPassportWatcher(),
        putUserConvertToPassportWatcher(),
    ]);
}

function* editStudentNameWatcher() {
    yield takeLatest(EDIT_STUDENT_NAME_REQUEST, editStudentNameSaga);
}

function* saveNewStudentWatcher() {
    yield takeEvery(SAVE_NEW_STUDENT_REQUEST, saveNewStudentSaga);
}

function* resetStudentLoginWatcher() {
    yield takeEvery(RESET_STUDENT_LOGIN_REQUEST, resetStudentLoginSaga);
}

function* deleteStudentWatcher() {
    yield takeEvery(DELETE_STUDENT_REQUEST, deleteStudentSaga);
}

function* getNewStudentLoginWatcher() {
    yield takeFirstCancelable(
        GET_NEW_STUDENT_LOGIN_REQUEST,
        CANCEL_ADDING_NEW_STUDENT,
        GET_NEW_STUDENT_LOGIN_SUCCESS,
        getNewStudentLoginSaga
    );
}

function* putUsersConvertToPassportWatcher() {
    yield takeLatest(CONVERT_STUDENTS_PASSPORT_REQUEST, putUsersConvertToPassportSaga);
}

function* putUserConvertToPassportWatcher() {
    yield takeLatest(CONVERT_STUDENT_PASSPORT_REQUEST, putUserConvertToPassportSaga);
}

export function* editStudentNameSaga(action) {
    const { id, name } = action;

    try {
        const teamId = getTeamIdFromPath();
        const { firstName, lastName } = yield call(patchStudent, id, { name });
        yield put(editStudentNameSuccess(id, firstName, lastName, teamId));
    } catch (error) {
        console.error(error);
        yield put(editStudentNameError(id));
        yield put(showNetworkError(error));
    }
}

const getRetryTimeout = (i) => {
    const retryTimeoutList = [200, 500, 1000];

    return retryTimeoutList[i];
};

export function* getNewStudentLoginSaga(action) {
    const { classId } = action;

    try {
        yield put(setRequesting(true));

        const { login, id: loginId } = yield retrySaga(
            function* retryFunc() {
                return yield call(postCreateStudentLogin, classId);
            },
            getRetryTimeout,
            3
        );

        yield put(getNewStudentLoginSuccess(login, loginId));
        yield put(setRequesting(false));
    } catch (error) {
        console.error(error);
        yield put(editStudentNameError(NEW_STUDENT_ID));
        yield put(showNetworkError(error));
        yield put(setRequesting(false));
    }
}

export function* saveNewStudentSaga(action) {
    const { classId, name, loginId } = action;

    try {
        yield put(setSubmitting(true));

        const { students } = yield call(postClassCreateStudent, classId, name, loginId, { getPinAndPass: true });

        // TODO: Прикрутить автоматическое создание паспорта для ученика
        yield put(saveNewStudentSuccess(students, classId));
        yield put(setCurrentEditingStudentIndex(null));
        yield put(setSubmitting(false));
    } catch (error) {
        console.error(error);
        yield put(showNetworkError(error));
        yield put(editStudentNameError(NEW_STUDENT_ID));
        yield put(setSubmitting(false));
    }
}

export function* resetStudentLoginSaga(action) {
    const { classId, studentId } = action;

    try {
        const { login } = yield call(postResetStudentLogin, studentId);
        yield put(resetStudentLoginSuccess(classId, studentId, login));
    } catch (error) {
        console.error(error);
        yield put(showNetworkError(error));
    }
}

export function* deleteStudentSaga(action) {
    const { classId, userId } = action;

    try {
        yield call(deleteStudent, classId, userId);
        yield put(deleteStudentSuccess(userId, classId));
    } catch (error) {
        console.error(error);
        yield put(showNetworkError(error));
    }
}

export function* convertUsersAndSaveSaga(action) {
    const { classId, users } = action;

    try {
        const usersWithPasswords = yield call(
            putUsersConvertToPassport,
            users.map((user) => user.id)
        );

        usersWithPasswords.forEach((user) => {
            setStudentPasswordToLocalStorageWithExpireTime(user.id, user.password);
        });

        yield put(getTeam(classId));
    } catch (error) {
        console.error(error);
        yield put(showNetworkError(error));
        throw error;
    }
}

export function* putUsersConvertToPassportSaga(action) {
    const { classId, users } = action;

    try {
        yield call(patchClass, classId, { use_passport_accounts: true });
        yield put(getTeam(classId));

        yield call(convertUsersAndSaveSaga, { classId, users });

        yield put(putUsersConvertToPassportSuccess());
    } catch (error) {
        yield put(putUsersConvertToPassportError());
    }
}

export function* putUserConvertToPassportSaga(action) {
    const { classId, user } = action;

    try {
        yield call(convertUsersAndSaveSaga, { classId, users: [user] });

        yield put(putUserConvertToPassportSuccess());
    } catch (error) {
        yield put(putUserConvertToPassportError());
    }
}
